import { observer } from "mobx-react-lite";
import { useContext, useEffect, useMemo, useRef } from "react";

import {
  dataAttribute,
  DataAttributes,
  IDetailsRowStyles,
  SelectionMode,
  Stack,
  useFormContext
} from "@bps/fluent-ui";
import { renderCalendarEventWrapper } from "@modules/booking/screens/booking-calendar/components/utils.tsx";
import { CalendarEvent } from "@stores/booking/models/CalendarEvent.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { InfiniteScrollList } from "@ui-components/InfiniteScrollList/InfiniteScrollList.tsx";
import { Selection } from "@ui-components/ShimmeredDetailsList/Selection.ts";

import { listWrapperStyles } from "./AppointmentInformationModal.styles.ts";
import { AppointmentInformationModalContext } from "./AppointmentInformationModalContext.ts";
import { ApptinfoFilterType } from "./AppointmentInformationModalInner.tsx";
import { PrefAndInformationPivotTabTypes } from "./AppointmentInformationPivotTabs.types.ts";
import { useGetCalendarEvents } from "./AppointmentInformationTable.hooks.tsx";
import { getAppointmentInformationTableColumns } from "./AppointmentInformationTable.utils.tsx";
import { AppointmentInformationTableNoResults } from "./AppointmentInformationTableNoResults.tsx";

interface AppointmentInformationTableProps {
  contactId: string;
  claimId: string | undefined;
  onDismiss: () => void;
  selectedTab: PrefAndInformationPivotTabTypes;
}

export const AppointmentInformationTable: React.FunctionComponent<AppointmentInformationTableProps> =
  observer(props => {
    const { contactId, claimId, selectedTab } = props;
    const root = useStores();
    const { booking, core } = root;
    const helper = useContext(AppointmentInformationModalContext);

    const selection = useRef(
      new Selection({
        onSelectionChanged: () => {
          const selectedItems = selection.current.getSelection();
          const selectedCount = selectedItems.length;

          if (selectedCount) {
            const selectedItem = selectedItems[0];
            if (selectedItem) {
              // Selection allows using items that don't have a "key" property, however then ShimmeredDetails
              //  list complains.  I don't want to do an app-wide refactor right now so I'm overriding the
              //  typing using as unknown
              helper.setSelectedCalendarEventId(
                (selectedItem as unknown as CalendarEvent).id
              );
            }
          }
          if (helper.selectionCount !== selectedCount) {
            helper.setSelectionCount(selectedCount);
          }
        }
      })
    );

    useEffect(() => {
      helper.setSelectionCount(undefined);
    }, [selectedTab, helper]);

    const columns = getAppointmentInformationTableColumns({
      core,
      claimId,
      onDismiss: props.onDismiss
    });

    const {
      state: { values }
    } = useFormContext<ApptinfoFilterType>();

    const getItems = useGetCalendarEvents(
      { contactId, claimId, selectedTab },
      values
    );

    const refreshKey = useMemo(() => {
      const calendarEventId = booking.ui.lastUpdatedCalendarEventData?.id;
      if (calendarEventId) {
        const calendarEvent = booking.calendarEventsMap.get(calendarEventId);
        if (
          calendarEvent?.attendees.some(
            attendee => attendee.attendeeId === contactId
          )
        ) {
          return calendarEvent.eTag;
        }
      }

      return "";
    }, [
      booking.calendarEventsMap,
      booking.ui.lastUpdatedCalendarEventData?.id,
      contactId
    ]);

    return (
      <Stack tokens={{ childrenGap: 8 }} styles={{ root: { height: "100%" } }}>
        <Stack
          styles={{
            root: {
              padding: 4,
              marginTop: 8
            }
          }}
        >
          <Stack
            styles={listWrapperStyles}
            {...dataAttribute(DataAttributes.Element, "list-wrapper")}
            grow
          >
            <InfiniteScrollList
              setKey="appt-table-key"
              getItems={getItems}
              refreshKey={refreshKey}
              cellAlignment="start"
              onRenderRow={props => {
                const detailRowStyles: Partial<IDetailsRowStyles> = {
                  checkCell: {
                    display: "inline-flex",
                    alignItems: "center"
                  }
                };

                return renderCalendarEventWrapper(
                  props,
                  undefined,
                  detailRowStyles
                );
              }}
              selection={selection.current}
              selectionMode={SelectionMode.single}
              selectionPreservedOnEmptyClick
              columns={columns}
              onRenderNoResults={() => (
                <AppointmentInformationTableNoResults
                  selectedTab={selectedTab}
                />
              )}
            />
          </Stack>
        </Stack>
      </Stack>
    );
  });
